import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import AdminNav from "../../components/AdminNav";
import RichTextEditor from "../../components/RichTextEditor";
import api from "../../utils/api";

const EMPTY_TEMPLATE = {
  id: null,
  name: "",
  key: "",
  process_key: "",
  description: "",
  subject: "",
  html_body: "<p></p>",
  variables: [],
  is_active: true,
  is_default: false,
  is_builtin: false,
};

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "blockquote"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    [{ color: [] }, { background: [] }],
    ["clean"],
  ],
};

const marketingQuillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "blockquote"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "image"],
    [{ color: [] }, { background: [] }],
    [{ align: [] }],
    ["clean"],
  ],
};

const MARKETING_FOOTER = `Tabram Eyewear uses your personal data for direct marketing purposes. If you do not want to receive &ldquo;Tabram Eyewear updates&rdquo; or &ldquo;Marketing communications&rdquo; you can click on <a href="{{unsubscribe_url}}">[Unsubscribe]</a>. To read up on your rights and find out more detailed information on how we use your personal data, please see our <a href="/privacy">[Privacy Policy]</a>`;
const MARKETING_LOGO_URL = "https://www.tabrameyewear.ie/logo.png";

function slugify(value) {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 120);
}

function formatTimestamp(value) {
  if (!value) {
    return "";
  }

  return new Date(value).toLocaleString();
}

export default function AdminEmails() {
  const [templates, setTemplates] = useState([]);
  const [processes, setProcesses] = useState([]);
  const [settings, setSettings] = useState({
    email_enabled: true,
    sendgrid_from_email: "",
    sendgrid_admin_email: "",
    sendgrid_reply_to_email: "",
    connectionStatus: {
      connected: false,
      mode: "Console Logging",
      apiKeyConfigured: false,
      apiKeyStoredInAdmin: false,
    },
  });
  const [loading, setLoading] = useState(true);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [templateForm, setTemplateForm] = useState(EMPTY_TEMPLATE);
  const [versions, setVersions] = useState([]);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [keyEditedManually, setKeyEditedManually] = useState(false);
  const [savedForm, setSavedForm] = useState(null);
  const [marketingRecipients, setMarketingRecipients] = useState("");
  const [marketingSubject, setMarketingSubject] = useState("");
  const [marketingHtml, setMarketingHtml] = useState("<p></p>");
  const [sendingMarketing, setSendingMarketing] = useState(false);
  const [savingMarketingTemplate, setSavingMarketingTemplate] = useState(false);
  const [productImages, setProductImages] = useState([]);
  const [imagePickerOpen, setImagePickerOpen] = useState(false);
  const [imagePickerCallback, setImagePickerCallback] = useState(null);

  const processOptions = useMemo(() => {
    return processes.reduce((accumulator, process) => {
      accumulator[process.key] = process;
      return accumulator;
    }, {});
  }, [processes]);

  const selectedProcess = templateForm.process_key ? processOptions[templateForm.process_key] : null;
  const isNewTemplate = templateForm.id === null;

  const isDirty = useMemo(() => {
    const comparableKeys = ["name", "key", "process_key", "description", "subject", "is_active", "is_default"];
    if (savedForm === null) {
      // new/duplicated unsaved form — dirty as soon as any meaningful field is filled
      return Boolean(templateForm.name || templateForm.subject || templateForm.html_body !== EMPTY_TEMPLATE.html_body);
    }
    const scalarDirty = comparableKeys.some((k) => {
      const a = Array.isArray(templateForm[k]) ? JSON.stringify(templateForm[k]) : String(templateForm[k] ?? "");
      const b = Array.isArray(savedForm[k]) ? JSON.stringify(savedForm[k]) : String(savedForm[k] ?? "");
      return a !== b;
    });
    if (scalarDirty) return true;
    // Compare html_body without leading/trailing whitespace to avoid Quill
    // normalisation differences on initial mount counting as a change.
    const htmlA = (templateForm.html_body ?? "").trim();
    const htmlB = (savedForm.html_body ?? "").trim();
    return htmlA !== htmlB;
  }, [templateForm, savedForm]);

  const applyTemplateSelection = useCallback((template) => {
    const normalized = {
      ...EMPTY_TEMPLATE,
      ...template,
      process_key: template.process_key || "",
      variables: Array.isArray(template.variables) ? template.variables : [],
    };
    setSelectedTemplateId(template.id);
    setTemplateForm(normalized);
    setSavedForm(normalized);
    setKeyEditedManually(true);
  }, []);

  const startNewTemplate = useCallback((availableProcesses = []) => {
    const firstProcess = availableProcesses[0]?.key || "";
    const fresh = {
      ...EMPTY_TEMPLATE,
      process_key: firstProcess,
      variables: availableProcesses[0]?.variables || [],
    };
    setSelectedTemplateId(null);
    setTemplateForm(fresh);
    setSavedForm(null);
    setVersions([]);
    setKeyEditedManually(false);
  }, []);

  const fetchBootstrap = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/emails/templates");
      const fetchedTemplates = data.templates || [];
      setTemplates(fetchedTemplates);
      setProcesses(data.processes || []);
      if (data.settings) {
        setSettings(data.settings);
      }

      if (fetchedTemplates.length > 0) {
        applyTemplateSelection(fetchedTemplates[0]);
      } else {
        startNewTemplate(data.processes || []);
      }
    } catch (error) {
      console.error("Error loading email manager:", error);
      toast.error(error.response?.data?.error || "Failed to load email manager");
    } finally {
      setLoading(false);
    }
  }, [applyTemplateSelection, startNewTemplate]);

  const fetchVersions = useCallback(async (templateId) => {
    setLoadingVersions(true);
    try {
      const { data } = await api.get(`/admin/emails/templates/${templateId}/versions`);
      setVersions(data.versions || []);
    } catch (error) {
      console.error("Error loading template versions:", error);
      toast.error(error.response?.data?.error || "Failed to load template history");
    } finally {
      setLoadingVersions(false);
    }
  }, []);

  useEffect(() => {
    fetchBootstrap();
  }, [fetchBootstrap]);

  useEffect(() => {
    if (selectedTemplateId) {
      fetchVersions(selectedTemplateId);
    } else {
      setVersions([]);
    }
  }, [fetchVersions, selectedTemplateId]);

  const refreshTemplates = async (preferredTemplateId = selectedTemplateId) => {
    const { data } = await api.get("/admin/emails/templates");
    setTemplates(data.templates || []);
    setProcesses(data.processes || []);
    if (data.settings) {
      setSettings(data.settings);
    }

    if (preferredTemplateId) {
      const nextSelection = (data.templates || []).find((template) => template.id === preferredTemplateId);
      if (nextSelection) {
        applyTemplateSelection(nextSelection);
        return;
      }
    }

    if ((data.templates || []).length > 0) {
      applyTemplateSelection(data.templates[0]);
    } else {
      startNewTemplate(data.processes || []);
    }
  };

  const handleSettingsChange = (event) => {
    const { name, value, type, checked } = event.target;
    setSettings((previous) => ({
      ...previous,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const saveSettings = async (event) => {
    event.preventDefault();
    setSavingSettings(true);
    try {
      const payload = {
        email_enabled: Boolean(settings.email_enabled),
        sendgrid_from_email: settings.sendgrid_from_email,
        sendgrid_admin_email: settings.sendgrid_admin_email,
        sendgrid_reply_to_email: settings.sendgrid_reply_to_email,
      };
      const { data } = await api.put("/admin/emails/settings", payload);
      setSettings(data.settings);
      toast.success("Email settings updated");
    } catch (error) {
      console.error("Error saving email settings:", error);
      toast.error(error.response?.data?.error || "Failed to save email settings");
    } finally {
      setSavingSettings(false);
    }
  };

  const handleTemplateFieldChange = (event) => {
    const { name, value, type, checked } = event.target;
    setTemplateForm((previous) => {
      const nextValue = type === "checkbox" ? checked : value;
      const nextState = {
        ...previous,
        [name]: nextValue,
      };

      if (name === "name" && !keyEditedManually && !previous.is_builtin) {
        nextState.key = slugify(value);
      }

      if (name === "key") {
        setKeyEditedManually(true);
      }

      if (name === "process_key") {
        const selected = processOptions[value];
        nextState.variables = selected ? selected.variables : [];
      }

      return nextState;
    });
  };

  const handleEditorChange = (html) => {
    setTemplateForm((previous) => ({ ...previous, html_body: html }));
  };

  const saveTemplate = async (event) => {
    event.preventDefault();
    setSavingTemplate(true);
    try {
      const payload = {
        name: templateForm.name,
        key: templateForm.key,
        process_key: templateForm.process_key || null,
        description: templateForm.description,
        subject: templateForm.subject,
        html_body: templateForm.html_body,
        variables: templateForm.variables,
        is_active: Boolean(templateForm.is_active),
        is_default: Boolean(templateForm.is_default),
      };

      if (isNewTemplate) {
        const { data } = await api.post("/admin/emails/templates", payload);
        toast.success("Template created");
        setSavedForm({ ...payload, id: data.template.id });
        await refreshTemplates(data.template.id);
      } else {
        const { data } = await api.put(`/admin/emails/templates/${templateForm.id}`, payload);
        toast.success("Template updated");
        setSavedForm({ ...payload, id: data.template.id });
        await refreshTemplates(data.template.id);
      }
    } catch (error) {
      console.error("Error saving template:", error);
      toast.error(error.response?.data?.error || "Failed to save template");
    } finally {
      setSavingTemplate(false);
    }
  };

  const deleteTemplate = async () => {
    if (!templateForm.id || templateForm.is_builtin) {
      return;
    }

    if (!window.confirm(`Delete template '${templateForm.name}'?`)) {
      return;
    }

    try {
      await api.delete(`/admin/emails/templates/${templateForm.id}`);
      toast.success("Template deleted");
      await refreshTemplates();
    } catch (error) {
      console.error("Error deleting template:", error);
      toast.error(error.response?.data?.error || "Failed to delete template");
    }
  };

  const duplicateTemplate = () => {
    if (!templateForm.name) {
      return;
    }

    const duplicated = {
      ...EMPTY_TEMPLATE,
      ...templateForm,
      id: null,
      is_builtin: false,
      is_default: false,
      name: `${templateForm.name} Copy`,
      key: slugify(`${templateForm.key}_copy`),
    };
    setSelectedTemplateId(null);
    setTemplateForm(duplicated);
    setSavedForm(null);
    setVersions([]);
    setKeyEditedManually(true);
    toast.info("Template copied into the editor — save when ready.");
  };

  const restoreVersion = async (versionId) => {
    if (!templateForm.id) {
      return;
    }

    if (!window.confirm("Restore this version? This will create a new revision entry.")) {
      return;
    }

    try {
      const { data } = await api.post(`/admin/emails/templates/${templateForm.id}/restore/${versionId}`);
      toast.success("Template restored");
      await refreshTemplates(data.template.id);
    } catch (error) {
      console.error("Error restoring template version:", error);
      toast.error(error.response?.data?.error || "Failed to restore template version");
    }
  };

  const sendTestEmail = async () => {
    if (!templateForm.id) {
      toast.error("Save the template before sending a test email");
      return;
    }

    setSendingTest(true);
    try {
      const { data } = await api.post(`/admin/emails/templates/${templateForm.id}/test`, { email: testEmail });
      toast.success(data.message || "Test email sent");
    } catch (error) {
      console.error("Error sending test email:", error);
      toast.error(error.response?.data?.error || "Failed to send test email");
    } finally {
      setSendingTest(false);
    }
  };

  const sendMarketingEmail = async (event) => {
    event.preventDefault();
    setSendingMarketing(true);
    try {
      const { data } = await api.post("/admin/emails/marketing", {
        recipients: marketingRecipients,
        subject: marketingSubject,
        html: marketingHtml,
      });
      toast.success(data.message || "Marketing email sent");
    } catch (error) {
      console.error("Error sending marketing email:", error);
      toast.error(error.response?.data?.error || "Failed to send marketing email");
    } finally {
      setSendingMarketing(false);
    }
  };

  const saveMarketingAsTemplate = async () => {
    if (!marketingSubject.trim() || !marketingHtml.replace(/<[^>]+>/g, "").trim()) {
      toast.error("Add a subject and email content before saving the template");
      return;
    }

    setSavingMarketingTemplate(true);
    try {
      const templateName = marketingSubject.trim().slice(0, 90);
      const { data } = await api.post("/admin/emails/templates", {
        name: templateName,
        key: slugify(`${templateName}_${Date.now()}`),
        process_key: null,
        description: "Marketing email template",
        subject: marketingSubject.trim(),
        html_body: marketingHtml,
        variables: [],
        is_active: true,
        is_default: false,
      });
      toast.success(data.message || "Marketing template saved");
      await refreshTemplates(data.template.id);
    } catch (error) {
      console.error("Error saving marketing template:", error);
      toast.error(error.response?.data?.error || "Failed to save marketing template");
    } finally {
      setSavingMarketingTemplate(false);
    }
  };

  const openImagePicker = useCallback((onSelect) => {
    setImagePickerCallback(() => onSelect);
    setImagePickerOpen(true);
    if (productImages.length === 0) {
      api.get("/upload/product-images").then(({ data }) => setProductImages(data.images || [])).catch(() => toast.error("Failed to load product images"));
    }
  }, [productImages.length]);

  const selectMarketingImage = (url) => {
    if (imagePickerCallback) imagePickerCallback(url);
    setImagePickerCallback(null);
    setImagePickerOpen(false);
  };

  const insertMarketingBlock = (block) => {
    setMarketingHtml((current) => `${current.replace(/<p><br><\/p>$/, "")}${block}<p><br></p>`);
  };

  const insertLogo = () => {
    insertMarketingBlock(`<p style="text-align: center"><img data-email-logo="true" src="${MARKETING_LOGO_URL}" alt="Tabram Eyewear" style="width: 240px; max-width: 100%; height: auto" /></p>`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white">
        <AdminNav />
        <div className="p-8 text-center text-gold-500">Loading email manager...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <AdminNav />
      <div className="p-8">
        <div className="max-w-7xl mx-auto space-y-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <h1 className="heading-metallic text-4xl mb-2">Admin Emails</h1>
              <p className="text-gray-400 max-w-3xl">Manage SendGrid-backed operational settings, built-in transactional templates, custom template variants, version history, and test sends from one admin module.</p>
            </div>
            <button
              type="button"
              onClick={() => startNewTemplate()}
              className="btn-primary"
            >
              New Template
            </button>
          </div>

          <form onSubmit={sendMarketingEmail} className="card space-y-5 border-gold-500/30">
            <div>
              <h2 className="heading-metallic-soft text-2xl">Marketing Email</h2>
              <p className="text-sm text-gray-400 mt-2">Design a one-off campaign and send it to up to 100 recipients. Each address receives a private delivery.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-300 mb-2">Recipient Emails</label>
                <textarea
                  value={marketingRecipients}
                  onChange={(event) => setMarketingRecipients(event.target.value)}
                  placeholder="name@example.com, another@example.com"
                  rows={2}
                  className="w-full bg-gray-950 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-gold-500"
                  required
                />
                <p className="text-xs text-gray-500 mt-2">Separate addresses with commas. Duplicate addresses are removed before sending.</p>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-300 mb-2">Subject</label>
                <input
                  type="text"
                  value={marketingSubject}
                  onChange={(event) => setMarketingSubject(event.target.value)}
                  maxLength={150}
                  className="w-full bg-gray-950 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-gold-500"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Email Content</label>
              <div className="bg-white text-black rounded-xl overflow-hidden border border-gray-700">
                <RichTextEditor
                  value={marketingHtml}
                  onChange={setMarketingHtml}
                  modules={marketingQuillModules}
                  imagePicker={openImagePicker}
                />
              </div>
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={() => openImagePicker((url) => insertMarketingBlock(`<p><img src="${url}" alt="" style="max-width: 100%; height: auto" /></p>`))} className="btn-secondary px-3 py-2 text-sm">Add Product Image</button>
                <button type="button" onClick={insertLogo} className="btn-secondary px-3 py-2 text-sm">Add Header Logo</button>
                <button type="button" onClick={() => insertMarketingBlock(`<p style="font-size: 12px; color: #666">${MARKETING_FOOTER}</p>`)} className="btn-secondary px-3 py-2 text-sm">Add Unsubscribe Footer</button>
              </div>
              <p className="text-xs text-gray-500 mt-2">Only images already linked to products can be inserted. Select an image to resize or delete it. Keep campaigns balanced with readable text and include your normal unsubscribe process.</p>
            </div>

            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <span className="text-xs text-gray-500">SendGrid will receive a separate personalization for each address.</span>
              <div className="flex flex-col sm:flex-row gap-3">
                <button type="button" onClick={saveMarketingAsTemplate} disabled={savingMarketingTemplate} className="btn-secondary">
                  {savingMarketingTemplate ? "Saving Template..." : "Save as Template"}
                </button>
                <button type="submit" disabled={sendingMarketing} className="btn-primary">
                  {sendingMarketing ? "Sending Campaign..." : "Send Marketing Email"}
                </button>
              </div>
            </div>
          </form>

          {imagePickerOpen && (
            <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Choose product image">
              <div className="bg-gray-900 border border-gray-700 rounded-xl p-6 max-w-4xl w-full max-h-[80vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-5">
                  <h2 className="heading-metallic-soft text-2xl">Choose Product Image</h2>
                  <button type="button" onClick={() => setImagePickerOpen(false)} className="btn-ghost">Close</button>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                  {productImages.map((image) => (
                    <button type="button" key={image.url} onClick={() => selectMarketingImage(image.url)} className="text-left border border-gray-700 rounded-lg p-2 hover:border-gold-500 transition">
                      <img src={image.url} alt={image.product_name || image.sku} className="w-full aspect-square object-contain bg-white rounded" />
                      <span className="block text-xs text-gray-300 truncate mt-2">{image.sku}</span>
                    </button>
                  ))}
                </div>
                {productImages.length === 0 && <p className="text-sm text-gray-400">No product images are available yet.</p>}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <form onSubmit={saveSettings} className="xl:col-span-1 card space-y-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="heading-metallic-soft text-2xl">Email Settings</h2>
                  <p className="text-sm text-gray-400 mt-2">The SendGrid API key remains environment-managed. Admin controls operational addresses and whether delivery is enabled.</p>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${settings.connectionStatus?.connected ? "bg-green-900 text-green-300" : "bg-yellow-900 text-yellow-300"}`}>
                  {settings.connectionStatus?.mode || "Console Logging"}
                </span>
              </div>

              <label className="flex items-center justify-between gap-4 p-4 bg-gradient-to-r from-black via-gray-900 to-black rounded-lg border border-gray-700">
                <div>
                  <div className="font-semibold">Email delivery enabled</div>
                  <div className="text-sm text-gray-400">Disable to stop all transactional emails without removing SendGrid configuration.</div>
                </div>
                <input
                  type="checkbox"
                  name="email_enabled"
                  checked={Boolean(settings.email_enabled)}
                  onChange={handleSettingsChange}
                  className="h-5 w-5 accent-gold-500"
                />
              </label>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">From Email</label>
                <input
                  type="email"
                  name="sendgrid_from_email"
                  value={settings.sendgrid_from_email || ""}
                  onChange={handleSettingsChange}
                  className="w-full bg-gray-950 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-gold-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Admin Inbox</label>
                <input
                  type="email"
                  name="sendgrid_admin_email"
                  value={settings.sendgrid_admin_email || ""}
                  onChange={handleSettingsChange}
                  className="w-full bg-gray-950 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-gold-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Reply-To Email</label>
                <input
                  type="email"
                  name="sendgrid_reply_to_email"
                  value={settings.sendgrid_reply_to_email || ""}
                  onChange={handleSettingsChange}
                  className="w-full bg-gray-950 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-gold-500"
                />
              </div>

              <button
                type="submit"
                disabled={savingSettings}
                className="btn-primary w-full"
              >
                {savingSettings ? "Saving Settings..." : "Save Email Settings"}
              </button>
            </form>

            <div className="xl:col-span-2 card">
              <div className="flex items-start justify-between gap-4 mb-6">
                <div>
                  <h2 className="heading-metallic-soft text-2xl">Template Library</h2>
                  <p className="text-sm text-gray-400 mt-2">Built-in templates remain tied to core processes. Custom templates can become the default active template for a process.</p>
                </div>
                <button
                  type="button"
                  onClick={duplicateTemplate}
                  disabled={!templateForm.name}
                  className="btn-secondary px-4 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Duplicate Selected
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-[320px,1fr] gap-6">
                <div className="space-y-3 max-h-[920px] overflow-y-auto pr-1">
                  {templates.map((template) => (
                    <button
                      key={template.id}
                      type="button"
                      onClick={() => applyTemplateSelection(template)}
                      className={`w-full text-left rounded-xl border p-4 transition ${selectedTemplateId === template.id ? "border-gold-500 bg-gradient-to-r from-black via-gray-900 to-black" : "border-gray-800 bg-gray-950 hover:border-gray-600"}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="font-semibold text-white">{template.name}</div>
                          <div className="text-xs text-gray-500 mt-1">{template.key}</div>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          {template.is_builtin && <span className="text-[11px] px-2 py-1 rounded-full bg-blue-900 text-blue-200">Built-In</span>}
                          {template.is_default && <span className="text-[11px] px-2 py-1 rounded-full bg-gold-500/20 text-gold-300">Default</span>}
                        </div>
                      </div>
                      <div className="mt-3 flex items-center justify-between text-xs text-gray-400">
                        <span>{processOptions[template.process_key]?.label || "Unassigned"}</span>
                        <span className={template.is_active ? "text-green-300" : "text-red-300"}>{template.is_active ? "Active" : "Inactive"}</span>
                      </div>
                    </button>
                  ))}
                </div>

                <div className="space-y-6">
                  <form onSubmit={saveTemplate} className="space-y-5">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <h3 className="heading-metallic-soft text-xl">{isNewTemplate ? "New Template" : templateForm.name || "Template Editor"}</h3>
                        <p className="text-sm text-gray-400 mt-1">{templateForm.is_builtin ? "Built-in template: key and process are locked." : "Custom template: assign to a process and optionally make it the default."}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Template Name</label>
                        <input
                          type="text"
                          name="name"
                          value={templateForm.name}
                          onChange={handleTemplateFieldChange}
                          className="w-full bg-gray-950 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-gold-500"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Template Key</label>
                        <input
                          type="text"
                          name="key"
                          value={templateForm.key}
                          onChange={handleTemplateFieldChange}
                          disabled={templateForm.is_builtin}
                          className="w-full bg-gray-950 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-gold-500 disabled:opacity-60"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Assigned Process</label>
                        <select
                          name="process_key"
                          value={templateForm.process_key || ""}
                          onChange={handleTemplateFieldChange}
                          disabled={templateForm.is_builtin}
                          className="w-full bg-gray-950 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-gold-500 disabled:opacity-60"
                        >
                          <option value="">Unassigned</option>
                          {processes.map((process) => (
                            <option key={process.key} value={process.key}>
                              {process.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Subject</label>
                        <input
                          type="text"
                          name="subject"
                          value={templateForm.subject}
                          onChange={handleTemplateFieldChange}
                          className="w-full bg-gray-950 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-gold-500"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
                      <textarea
                        name="description"
                        value={templateForm.description || ""}
                        onChange={handleTemplateFieldChange}
                        rows={2}
                        className="w-full bg-gray-950 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-gold-500"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <label className="flex items-center gap-3 p-4 bg-gray-950 rounded-lg border border-gray-800">
                        <input
                          type="checkbox"
                          name="is_active"
                          checked={Boolean(templateForm.is_active)}
                          onChange={handleTemplateFieldChange}
                          className="h-4 w-4 accent-gold-500"
                        />
                        <span>Template is active</span>
                      </label>
                      <label className="flex items-center gap-3 p-4 bg-gray-950 rounded-lg border border-gray-800">
                        <input
                          type="checkbox"
                          name="is_default"
                          checked={Boolean(templateForm.is_default)}
                          onChange={handleTemplateFieldChange}
                          disabled={!templateForm.process_key}
                          className="h-4 w-4 accent-gold-500"
                        />
                        <span>Use as default for assigned process</span>
                      </label>
                    </div>

                    <div className="bg-gray-950 border border-gray-800 rounded-xl p-4">
                      <div className="flex items-center justify-between gap-4 mb-3">
                        <div>
                          <div className="text-sm font-semibold text-gray-200">Available Variables</div>
                          <div className="text-xs text-gray-500 mt-1">Use double curly braces, for example: {"{{first_name}}"}</div>
                        </div>
                        {selectedProcess && <div className="text-xs text-gray-500">{selectedProcess.description}</div>}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {(templateForm.variables || []).map((variable) => (
                          <span key={variable} className="px-3 py-1 rounded-full bg-gradient-to-r from-black via-gray-900 to-black border border-gray-700 text-sm text-gold-300">
                            {`{{${variable}}}`}
                          </span>
                        ))}
                        {(templateForm.variables || []).length === 0 && <span className="text-sm text-gray-500">No process variables assigned.</span>}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Template Body</label>
                      <div className="bg-white text-black rounded-xl overflow-hidden border border-gray-700">
                        <RichTextEditor value={templateForm.html_body} onChange={handleEditorChange} modules={quillModules} />
                      </div>
                    </div>

                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div className="text-xs text-gray-500">
                        {templateForm.updated_at ? `Last updated ${formatTimestamp(templateForm.updated_at)}` : "Template has not been saved yet."}
                        {isDirty && <span className="ml-2 text-yellow-400">Unsaved changes</span>}
                      </div>
                      <div className="flex items-center gap-3">
                        {!isNewTemplate && !templateForm.is_builtin && (
                          <button
                            type="button"
                            onClick={deleteTemplate}
                            className="px-4 py-2 rounded-lg border border-red-700 text-red-300 hover:bg-red-900/30 transition"
                          >
                            Delete
                          </button>
                        )}
                        <button
                          type="submit"
                          disabled={savingTemplate || !isDirty}
                          className="btn-primary"
                        >
                          {savingTemplate ? "Saving..." : isNewTemplate ? "Save Template" : "Save Changes"}
                        </button>
                      </div>
                    </div>
                  </form>

                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                    <div className="bg-gray-950 border border-gray-800 rounded-xl p-4 space-y-4">
                      <div className="flex items-center justify-between gap-4">
                        <h4 className="heading-metallic-soft text-lg">Preview</h4>
                        <span className="text-xs text-gray-500">Rendered with stored HTML</span>
                      </div>
                      <div className="bg-white text-black rounded-lg min-h-[280px] p-4 overflow-auto" dangerouslySetInnerHTML={{ __html: templateForm.html_body }} />
                    </div>

                    <div className="bg-gray-950 border border-gray-800 rounded-xl p-4 space-y-4">
                      <div>
                        <h4 className="heading-metallic-soft text-lg">Test Send</h4>
                        <p className="text-sm text-gray-400 mt-1">Sends the selected template with sample data. Save the template first if you want to test new changes.</p>
                      </div>
                      <input
                        type="email"
                        value={testEmail}
                        onChange={(event) => setTestEmail(event.target.value)}
                        placeholder="name@example.com"
                        className="w-full bg-black border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-gold-500"
                      />
                      <button
                        type="button"
                        onClick={sendTestEmail}
                        disabled={sendingTest || !testEmail}
                        className="btn-secondary w-full"
                      >
                        {sendingTest ? "Sending Test..." : "Send Test Email"}
                      </button>
                    </div>
                  </div>

                  <div className="bg-gray-950 border border-gray-800 rounded-xl p-4 space-y-4">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <h4 className="heading-metallic-soft text-lg">Version History</h4>
                        <p className="text-sm text-gray-400 mt-1">Every create, update, and restore action records a snapshot for this template.</p>
                      </div>
                      {loadingVersions && <span className="text-sm text-gray-500">Loading history...</span>}
                    </div>

                    <div className="space-y-3 max-h-72 overflow-y-auto">
                      {versions.map((version) => (
                        <div key={version.id} className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 p-4 rounded-lg border border-gray-800 bg-black/60">
                          <div>
                            <div className="font-semibold text-white">Version {version.version_number}</div>
                            <div className="text-sm text-gray-400">{version.action} on {formatTimestamp(version.created_at)}</div>
                            <div className="text-xs text-gray-500 mt-1">{version.subject}</div>
                          </div>
                          <button
                            type="button"
                            onClick={() => restoreVersion(version.id)}
                            disabled={!templateForm.id}
                            className="px-4 py-2 rounded-lg border border-gray-600 text-sm font-semibold text-gray-200 hover:border-gold-500 hover:text-gold-400 transition disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Restore
                          </button>
                        </div>
                      ))}
                      {versions.length === 0 && !loadingVersions && <div className="text-sm text-gray-500">No version history yet.</div>}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}